/**
 * regionRecords: a sub-region as a RECORD (app/region-records.md).
 *
 * A sub-region ("F12.10.34", "NOE-004") used to exist only as every drawn
 * shape sharing a type and a name, with its description, properties and
 * state copied onto each shape. Now it is a record of its own, and each
 * drawn shape (a "part") points at it by `recordId`.
 *
 *   record  { id, typeId, name, description, properties, stateId,
 *             owner, dates, parentId, links, history,
 *             createdAt, createdBy, updatedAt, updatedBy }
 *   shape   { id, recordId, regionType, subRegionName, stateId, geometry… }
 *
 * `regionType` / `subRegionName` stay on each shape as a MIRROR of the
 * record, because the renderers, labels, filters and exports read them;
 * the record is the truth and its writers keep the mirrors in step.
 *
 * STATES. A region type lists as many states as it needs. Its
 * `stateScope` says where the current state lives:
 *   'parts'  (default)  each drawn part has its own state, so an NOE can be
 *                       partly energised; the record shows the breakdown.
 *   'single'            the sub-region has ONE state (record.stateId), and
 *                       every part shows it.
 *
 * Pure: no React, no storage. Pinned by regionRecords.eval.js.
 */

export const UNNAMED = 'Unnamed';

export const nameOfShape = (shape) => (shape && shape.subRegionName) || UNNAMED;

export const stateScopeOf = (type) => (type && type.stateScope === 'single' ? 'single' : 'parts');

/**
 * Where one sub-region of this type LIVES:
 *   'project'  (default)  one record across every document: a Subsystem
 *                         F12.10.34 drawn on three P&IDs is ONE sub-region.
 *   'document'            one record PER DOCUMENT: "Redline 1" on one drawing
 *                         and "Redline 1" on another are unrelated. The record
 *                         carries the `filename` it belongs to.
 */
export const recordScopeOf = (type) => (type && type.recordScope === 'document' ? 'document' : 'project');

export function newRecordId() {
  return `rec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const typeByName = (types, name) => (types || []).find(t => t && t.name === name) || null;
export const typeById = (types, id) => (types || []).find(t => t && t.id === id) || null;

/** The type a record belongs to (by id; a record made before its type had an id falls back to the name). */
export const typeOfRecord = (types, record) =>
  (record && (typeById(types, record.typeId) || typeByName(types, record.typeName))) || null;

/** The state a type counts as DONE: one marked `meaning: 'done'`, else the last one. */
export function doneStateId(type) {
  const states = (type && Array.isArray(type.states)) ? type.states : [];
  if (!states.length) return null;
  const marked = states.find(s => s && s.meaning === 'done');
  return (marked || states[states.length - 1]).id || null;
}

export function makeRecord({ typeId = null, typeName = null, name, description = '', properties = {}, stateId = null, filename = null, by = null, at = null }) {
  const when = at || new Date().toISOString();
  return {
    id: newRecordId(),
    typeId,
    ...(typeId ? {} : { typeName }),
    // A document-scoped type's record belongs to one document (recordScopeOf).
    ...(filename ? { filename } : {}),
    name: name || UNNAMED,
    description: description || '',
    properties: { ...(properties || {}) },
    stateId: stateId || null,
    owner: null,
    dates: {},
    parentId: null,
    links: [],
    history: [],
    createdAt: when,
    createdBy: by,
    updatedAt: when,
    updatedBy: by,
  };
}

const keyOf = (typeKey, name) => `${typeKey}::${name}`;
const typeKeyOf = (types, typeName) => {
  const t = typeByName(types, typeName);
  return t && t.id ? `id:${t.id}` : `name:${typeName || ''}`;
};
const recordTypeKey = (record) => (record.typeId ? `id:${record.typeId}` : `name:${record.typeName || ''}`);
// The key a record is found by: type + name, and its document for a
// document-scoped type.
const docPart = (docScoped, filename) => (docScoped ? `@@${filename || ''}` : '');
const recordKeyOf = (types, record) => {
  const t = typeOfRecord(types, record);
  return keyOf(recordTypeKey(record), record.name) + docPart(recordScopeOf(t) === 'document', record.filename);
};

/**
 * Bring shapes and records into step. Used once on load (the conversion of
 * data from before records existed) and after every remote merge, so it
 * must be cheap and change nothing when everything already agrees.
 *
 * - Every real shape ends up with a `recordId` whose record exists. A shape
 *   with none is matched to a record by type + name, or a record is made
 *   from it (its description, properties and, for a one-state type, its
 *   state).
 * - A placeholder shape (the old "planned sub-region" stand-in) becomes a
 *   record with no parts, and the placeholder is dropped.
 * - A type's `plannedSubRegions` names become records with no parts.
 * - A shape whose name / type no longer matches its record was MOVED to
 *   another sub-region (the edit dialog re-files a shape by renaming it),
 *   so it joins the record of its new name. Renaming the sub-region itself
 *   goes through renameRecord, which changes the record and every part
 *   together, so the two never disagree.
 * - A record made before its type had an id is given the id once the type
 *   exists (or it would be keyed apart from the shapes and duplicated).
 *
 * Returns { records, shapes, newRecords, changedRecords, changedShapes,
 * droppedShapeIds }. Inputs are never mutated.
 */
export function reconcileRecords(shapes, records, types, { by = null, at = null, keepPlaceholders = false } = {}) {
  const changedRecords = [];
  const outRecords = (records || []).map(r => {
    if (r && !r.typeId && r.typeName) {
      const t = typeByName(types, r.typeName);
      if (t && t.id) {
        const { typeName: _drop, ...rest } = r; // eslint-disable-line no-unused-vars
        const up = { ...rest, typeId: t.id };
        changedRecords.push(up);
        return up;
      }
    }
    return r;
  });
  const byId = new Map(outRecords.map(r => [r.id, r]));
  const byKey = new Map(outRecords.map(r => [recordKeyOf(types, r), r]));
  const newRecords = [];
  const changedShapes = [];
  const droppedShapeIds = [];

  const ensure = (typeName, name, seed) => {
    const tk = typeKeyOf(types, typeName);
    const docScoped = recordScopeOf(typeByName(types, typeName)) === 'document';
    const filename = docScoped ? ((seed && seed.filename) || null) : null;
    const k = keyOf(tk, name) + docPart(docScoped, filename);
    let rec = byKey.get(k);
    if (!rec) {
      const type = typeByName(types, typeName);
      const described = (type && type.subRegionDescriptions && type.subRegionDescriptions[name]) || '';
      rec = makeRecord({
        typeId: type ? type.id || null : null,
        typeName: type && type.id ? null : typeName,
        name,
        description: (seed && seed.subRegionDescription) || described,
        properties: (seed && seed.properties) || {},
        stateId: stateScopeOf(type) === 'single' ? ((seed && seed.stateId) || null) : null,
        filename,
        by,
        at: (seed && seed.createdAt) || at,
      });
      outRecords.push(rec);
      newRecords.push(rec);
      byId.set(rec.id, rec);
      byKey.set(k, rec);
    }
    return rec;
  };

  const outShapes = [];
  for (const s of shapes || []) {
    if (!s) continue;
    if (s.isPlaceholder && !keepPlaceholders) {
      ensure(s.regionType, nameOfShape(s), s);
      droppedShapeIds.push(s.id);
      continue;
    }
    // keepPlaceholders: a surface still lists sub-regions from shapes, so
    // the stand-in stays, linked to its record like any other shape.
    let rec = s.recordId ? byId.get(s.recordId) : null;
    if (rec) {
      const t0 = typeOfRecord(types, rec);
      const recType = t0 ? t0.name : rec.typeName;
      // Re-filed under another name or type: it belongs to THAT record now.
      if (nameOfShape(s) !== rec.name || (s.regionType && recType && s.regionType !== recType)) rec = null;
      else if (recordScopeOf(t0) === 'document' && rec.filename && s.filename && rec.filename !== s.filename) rec = null;
    }
    if (!rec) rec = ensure(s.regionType, nameOfShape(s), s);
    const type = typeOfRecord(types, rec);
    const wantType = type ? type.name : (rec.typeName || s.regionType);
    if (s.recordId !== rec.id || s.subRegionName !== rec.name || s.regionType !== wantType) {
      const next = { ...s, recordId: rec.id, subRegionName: rec.name, regionType: wantType };
      outShapes.push(next);
      changedShapes.push(next);
    } else {
      outShapes.push(s);
    }
  }

  for (const t of types || []) {
    for (const planned of (t && t.plannedSubRegions) || []) {
      const name = typeof planned === 'string' ? planned : planned && planned.name;
      if (name) ensure(t.name, name, null);
    }
  }

  return { records: outRecords, shapes: outShapes, newRecords, changedRecords, changedShapes, droppedShapeIds };
}

/**
 * Rename a sub-region: the record and every part's name mirror, together.
 * Returns null when another record of the same type already has the name
 * (two sub-regions must never share one), else { records, shapes,
 * record, changedShapes }.
 */
export function renameRecord(records, shapes, recordId, newName, { by = null, at = null } = {}) {
  const name = String(newName || '').trim();
  const rec = (records || []).find(r => r && r.id === recordId);
  if (!rec || !name) return null;
  const clash = (records || []).some(r => r && r.id !== recordId && recordTypeKey(r) === recordTypeKey(rec) && r.name === name
    && (!rec.filename || !r.filename || r.filename === rec.filename));
  if (clash) return null;
  const record = withRecordEdit(rec, { name }, { by, at });
  const changedShapes = [];
  const outShapes = (shapes || []).map(sh => {
    if (!sh || sh.recordId !== recordId || sh.subRegionName === name) return sh;
    const next = { ...sh, subRegionName: name };
    changedShapes.push(next);
    return next;
  });
  return { records: records.map(r => (r.id === recordId ? record : r)), shapes: outShapes, record, changedShapes };
}

/** A record's drawn parts. */
export const partsOf = (record, shapes) => (shapes || []).filter(s => s && !s.isPlaceholder && s.recordId === record.id);

/**
 * Where a sub-region is up to.
 *   single:  { scope: 'single', stateId, done }
 *   parts:   { scope: 'parts', total, counts: { [stateId]: n }, none, done, mixed, stateId }
 *            `stateId` is the state every part shares, or null when they
 *            differ or there are no parts. `done` is true only when every
 *            part is in the done state.
 */
export function stateSummary(record, shapes, type) {
  const done = doneStateId(type);
  if (stateScopeOf(type) === 'single') {
    return { scope: 'single', stateId: record.stateId || null, done: !!done && record.stateId === done };
  }
  const parts = partsOf(record, shapes);
  const counts = {};
  let none = 0;
  for (const p of parts) {
    if (p.stateId) counts[p.stateId] = (counts[p.stateId] || 0) + 1;
    else none += 1;
  }
  const ids = Object.keys(counts);
  const shared = parts.length > 0 && none === 0 && ids.length === 1 ? ids[0] : null;
  return {
    scope: 'parts',
    total: parts.length,
    counts,
    none,
    stateId: shared,
    mixed: ids.length + (none ? 1 : 0) > 1,
    done: !!done && parts.length > 0 && shared === done,
  };
}

/**
 * A record with its state changed and the change written into its history.
 * For a one-state type `stateId` is the record's own; for a parts type pass
 * the part ids that moved, and the record keeps no state of its own.
 */
export function withStateChange(record, { from = null, to = null, parts = null, by = null, at = null, note = '' } = {}) {
  const when = at || new Date().toISOString();
  const entry = { at: when, by, from, to, ...(parts && parts.length ? { parts: [...parts] } : {}), ...(note ? { note } : {}) };
  return {
    ...record,
    ...(parts && parts.length ? {} : { stateId: to }),
    history: [...(record.history || []), entry],
    updatedAt: when,
    updatedBy: by,
  };
}

/** A record with some fields changed, stamped. */
// ── Completions tracking (roadmap: the sub-region as a real tracker) ──
// The tick map: record.objectTicks[objectId] = { at, by } while ticked.
export function withTick(record, objectId, done, { by = null, at = null } = {}) {
  const cur = (record && record.objectTicks && typeof record.objectTicks === 'object') ? record.objectTicks : {};
  if (!!cur[objectId] === !!done) return record;
  const next = { ...cur };
  if (done) next[objectId] = { at: at || new Date().toISOString(), by };
  else delete next[objectId];
  return withRecordEdit(record, { objectTicks: next }, { by, at });
}

// Progress against the objects INSIDE NOW: a tick for an object that has
// left the boundary stops counting (and counts again if it comes back).
export function tickProgress(record, objectIds) {
  const ticks = (record && record.objectTicks) || {};
  const ids = objectIds || [];
  let done = 0;
  for (const id of ids) if (ticks[id]) done += 1;
  return { done, total: ids.length };
}

// Overdue: a target date is set, the day has PASSED, and it is not done.
// The target day itself is not overdue — a date means "by the end of".
export function targetOverdue(record, { done = false, now = Date.now() } = {}) {
  const t = record && record.dates ? Date.parse(record.dates.target || '') : NaN;
  if (!Number.isFinite(t) || done) return false;
  return now >= t + 24 * 60 * 60 * 1000;
}

export function withRecordEdit(record, patch, { by = null, at = null } = {}) {
  const when = at || new Date().toISOString();
  return { ...record, ...patch, updatedAt: when, updatedBy: by };
}

/**
 * The record a (type, name) pair names, if any. For a document-scoped type
 * pass the document's `filename`: the same name on another document is
 * another sub-region. Without one, the first of that name.
 */
export function findRecord(records, types, typeName, name, filename = null) {
  const tk = typeKeyOf(types, typeName);
  const n = name || UNNAMED;
  const docScoped = recordScopeOf(typeByName(types, typeName)) === 'document';
  return (records || []).find(r => r && recordTypeKey(r) === tk && r.name === n
    && (!docScoped || !filename || r.filename === filename)) || null;
}

/** Parts whose state changed between two versions of the shapes: [{ id, recordId, from, to }]. */
export function stateChangesBetween(prevShapes, nextShapes) {
  const before = new Map((prevShapes || []).filter(Boolean).map(s => [s.id, s]));
  const out = [];
  for (const s of nextShapes || []) {
    if (!s || s.isPlaceholder || !s.recordId) continue;
    const p = before.get(s.id);
    if (!p) continue; // a new part: its state is where it starts, not a change
    const from = p.stateId || null;
    const to = s.stateId || null;
    if (from !== to) out.push({ id: s.id, recordId: s.recordId, from, to });
  }
  return out;
}

/**
 * Write state changes into their records' history, and hold a one-state
 * type to ONE state: a part moved there moves the record and every other
 * part with it (their colours follow the state's swatch, as a state change
 * always painted them). Parts types keep each part's own state and log the
 * move with the parts that moved, one entry per from / to pair.
 * Returns { records, shapes, changedRecords, changedShapes }.
 */
export function applyStateChanges(records, shapes, changes, types, { by = null, at = null } = {}) {
  if (!changes || !changes.length) return { records, shapes, changedRecords: [], changedShapes: [] };
  const when = at || new Date().toISOString();
  const byRecord = new Map();
  for (const c of changes) {
    if (!byRecord.has(c.recordId)) byRecord.set(c.recordId, []);
    byRecord.get(c.recordId).push(c);
  }
  const changedRecords = [];
  const pinned = new Map(); // recordId -> { to, color } for one-state types
  const outRecords = (records || []).map(r => {
    const list = r && byRecord.get(r.id);
    if (!list) return r;
    const type = typeOfRecord(types, r);
    let next = r;
    if (stateScopeOf(type) === 'single') {
      const to = list[list.length - 1].to;
      if ((r.stateId || null) === to) return r;
      next = withStateChange(r, { from: r.stateId || null, to, by, at: when });
      const def = (type && type.states || []).find(s => s && s.id === to);
      pinned.set(r.id, { to, color: def ? (def.color || def.fillColor || null) : null });
    } else {
      const groups = new Map();
      for (const c of list) {
        const k = `${c.from}>${c.to}`;
        if (!groups.has(k)) groups.set(k, { from: c.from, to: c.to, parts: [] });
        groups.get(k).parts.push(c.id);
      }
      for (const g of groups.values()) next = withStateChange(next, { from: g.from, to: g.to, parts: g.parts, by, at: when });
    }
    changedRecords.push(next);
    return next;
  });
  const changedShapes = [];
  const outShapes = pinned.size ? (shapes || []).map(s => {
    const pin = s && pinned.get(s.recordId);
    if (!pin || s.isPlaceholder || (s.stateId || null) === pin.to) return s;
    const next = { ...s, stateId: pin.to, ...(pin.color ? { fillColor: pin.color, borderColor: pin.color } : {}) };
    changedShapes.push(next);
    return next;
  }) : shapes;
  return { records: outRecords, shapes: outShapes, changedRecords, changedShapes };
}
